// Bilansowanie energii: spółdzielnia raz w miesiącu fakturuje członków za MWh, które zbilansowała dla ich PPE.
// Czysta logika widoku Bilansowanie — bez wywołań API, żeby dało się ją testować.

export const MONTH_NAMES = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

export const BALANCING_INVOICE = 'balancing_invoice';

// Model wybiera operator dla instancji; administrator widzi, który obowiązuje
export const BALANCING_MODEL_LABELS = {
    cost_split: 'ZSE: miesięczne koszty dzielone na PPE',
    fixed_rate: 'Sudecka SE: stała stawka za MWh',
};

export const monthLabel = (year, month) => `${MONTH_NAMES[month - 1]} ${year}`;
export const monthKey = (year, month) => `${year}-${String(month).padStart(2, '0')}`;

export const previousMonth = (date) => {
    const previous = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    return { year: previous.getFullYear(), month: previous.getMonth() + 1 };
};

// Jeden wiersz na miesiąc: wpisane koszty, rozliczenie miesiąca i czy faktury za bilansowanie już wyszły
export const monthRows = ({ costs = [], billingRuns = [], invoices = [] }) => {
    const rows = new Map();
    const row = (year, month) => {
        const key = monthKey(year, month);
        if (!rows.has(key)) {
            rows.set(key, { key, year, month, cost: null, run: null, balancingIssued: false });
        }
        return rows.get(key);
    };

    costs.forEach((cost) => {
        row(cost.year, cost.month).cost = cost.amount;
    });
    billingRuns.forEach((run) => {
        row(run.year, run.month).run = run;
    });

    const runsById = Object.fromEntries(billingRuns.map((run) => [run.id, run]));
    invoices
        .filter((invoice) => invoice.kind === BALANCING_INVOICE)
        .forEach((invoice) => {
            const run = runsById[invoice.billing_run_id];
            if (run) row(run.year, run.month).balancingIssued = true;
        });

    return [...rows.values()].sort((a, b) => b.key.localeCompare(a.key));
};

// Stawka za miesiąc jest znana: w modelu stałej stawki z ustawień, w modelu ZSE z wpisanych kosztów
const priced = (row, settings) =>
    settings.balancing_model === 'fixed_rate' ? settings.balancing_rate_per_mwh != null : row.cost != null;

// Te same warunki sprawdza POST /invoices/generate-balancing
export const canIssueBalancing = (row, settings) =>
    Boolean(
        settings?.balancing_active
            && row.run?.status === 'completed'
            && !row.balancingIssued
            && priced(row, settings)
    );

// Kwota jak w backendzie (BalancingCostIn, balancing_rate_per_mwh): dodatnia, najwyżej dwa miejsca po przecinku
export const normalizeAmount = (value) => String(value ?? '').trim().replace(',', '.');

export const validateAmount = (value, noun = 'Kwota') => {
    const text = normalizeAmount(value);
    if (!text) return `${noun} jest wymagana`;
    if (!/^\d+(\.\d{1,2})?$/.test(text)) return `${noun} musi być liczbą z najwyżej dwoma miejscami po przecinku`;
    if (Number(text) <= 0) return `${noun} musi być dodatnia`;
    return '';
};

// Backend nazywa okres „2026/9”; po polsku to „wrzesień 2026”
const periodLabel = (text) => {
    const [year, month] = text.split('/').map(Number);
    return month >= 1 && month <= 12 ? `${MONTH_NAMES[month - 1].toLowerCase()} ${year}` : text;
};

// Komunikaty backendu przychodzą po angielsku (routers/invoices.py, schemas/instance_settings.py)
const TRANSLATIONS = [
    [/^Balancing is not enabled on this instance/i,
        () => 'Bilansowanie jest wyłączone w tej instancji — włącza je operator'],
    [/^Billing for (\S+) is not completed/i,
        ([, period]) => `Rozliczenie za ${periodLabel(period)} nie jest zakończone — najpierw wygeneruj faktury za energię`],
    [/^Balancing invoices for (\S+) are already issued; delete the billing run/i,
        ([, period]) => `Faktury za bilansowanie za ${periodLabel(period)} są już wystawione — żeby zmienić koszty, usuń rozliczenie tego miesiąca`],
    [/^Balancing invoices for (\S+) are already issued/i,
        ([, period]) => `Faktury za bilansowanie za ${periodLabel(period)} są już wystawione`],
    [/^The balancing cost for (\S+) is not entered/i,
        ([, period]) => `Brak kosztów bilansowania za ${periodLabel(period)}`],
    [/^Not enough permissions/i, () => 'Brak uprawnień — to konto ma dostęp tylko do odczytu'],
    [/^Input should be greater than 0/i, () => 'Kwota musi być dodatnia'],
    [/no more than 2 decimal places/i, () => 'Kwota może mieć najwyżej dwa miejsca po przecinku'],
    [/^Extra inputs are not permitted/i, () => 'To ustawienie zmienia tylko operator'],
];

export const translateBalancingError = (message) => {
    if (!message) return message;
    for (const [pattern, translate] of TRANSLATIONS) {
        const match = message.match(pattern);
        if (match) return translate(match);
    }
    return message;
};

// 4xx z FastAPI: detail to tekst albo lista błędów walidacji z „Value error, ” przed komunikatem
export const describeApiError = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return translateBalancingError(detail);
    if (Array.isArray(detail) && detail.length > 0) {
        return detail
            .map(({ msg }) => translateBalancingError(String(msg).replace(/^Value error, /, '')))
            .join(', ');
    }
    return fallback;
};

export const pluralInvoices = (count) => {
    if (count === 1) return '1 faktura';
    const last = count % 10;
    const lastTwo = count % 100;
    if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return `${count} faktury`;
    return `${count} faktur`;
};

// Odpowiedź POST /invoices/generate-balancing ma kształt rozliczenia miesiąca: {total, succeeded, failed, details}.
// Gdy choć jedna faktura się nie uda, backend cofa pozostałe — nic nie zostaje wystawione.
export const describeIssueResult = (result, year, month) => {
    const period = monthLabel(year, month).toLowerCase();
    if (!result.total) {
        return { ok: true, message: `Za ${period} żadne PPE nie uczestniczyło w bilansowaniu — nie ma czego fakturować` };
    }
    if (result.failed) {
        const errors = (result.details || []).filter((detail) => !detail.success).map((detail) => detail.error).filter(Boolean);
        const reasons = errors.length ? `: ${errors.join('; ')}` : '';
        return {
            ok: false,
            message: `Nie udało się wystawić faktur za bilansowanie za ${period} (${result.failed} z ${pluralInvoices(result.total)})${reasons}`,
        };
    }
    return { ok: true, message: `Wystawiono ${pluralInvoices(result.succeeded)} za bilansowanie za ${period}` };
};
